import React from 'react';
import { styled, StyledProps } from '@glitz/react';
import connect from 'Shared/connect';
import { formatPrice } from 'Shared/number-format';
import { LanguageCodes, translate } from '@polarnopyret/scope';
import * as style from 'Shared/Style';

type RequiredPropType = {
  sellingPrice: number;
  listPrice?: number;
  comparePrice?: number;
  currentPriceHigh?: number;
  currentPriceLow?: number;
  originalPriceHigh?: number;
  originalPriceLow?: number;
  comparePriceHigh?: number;
  comparePriceLow?: number;
  fontSize?: number;
  fontSizePrevPrice?: number;
  fontWeight?: number;
  vertical?: boolean;
  spaceBetween?: boolean;
  className?: string;
  microData?: boolean;
  explicitCulture?: string;
  explicitCurrency?: string;
  noStyle?: boolean;
  subtraction?: boolean;
  displayPriceLabels?: boolean;
  rowBreakMainPrice?: boolean;
  isPdp?: boolean;
};

type PropType = RequiredPropType &
  StyledProps & {
    culture: string;
    currency: string;
    countryCode: string;
  };

const OldPrice = styled.span({
  color: style.colors.grey.dark
});

const PriceWrap = styled.div({
  marginBottom: '18px'
});

const ItemPrice = styled.div({
  fontSize: '20px',
  letterSpacing: '-.005em',
  lineHeight: 1,
  marginBottom: '6px',
  display: 'flex',
  alignItems: 'baseline',
  gap: '10px',
  flexWrap: 'wrap',
  fontWeight: 500,
  ['@media ' + style.mediaMinLarge]: {
    fontSize: '22px',
  },
});

const Percent = styled.span({
  letterSpacing: '.04em',
  fontSize: '16px',
  fontWeight: 'normal',
  textTransform: 'uppercase',
  display: 'flex',
  gap: '10px',
})

const PrevPrice = styled.div({
  color: style.colors.grey.dark,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  fontSize: '14px',
  ['@media ' + style.mediaMinLarge]: {
    fontWeight: 500,
  },
});

const PrevPricePrice = styled.s({
  color: style.colors.grey.medium,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  fontSize: '14px',
  ['@media ' + style.mediaMinLarge]: {
    fontWeight: 500,
  },
});

const ComparePrice = styled.div({
  color: style.colors.grey.dark,
  fontSize: '12px',
  letterSpacing: '.02em',
  marginTop: '5px',
  lineHeight: 1.4,
  ['@media ' + style.mediaMinLarge]: {
    fontSize: '11px',
    fontWeight: 500,
  },
});

export default styled(
  connect(state => ({
    culture: state.appShellData.culture,
    currency: state.appShellData.market.currency,
    countryCode: state.appShellData.context.countryCode,
  }))(
    class extends React.PureComponent<PropType> {
      render() {
        const {
          compose,
          sellingPrice,
          listPrice = 0,
          comparePrice = 0,
          currentPriceLow,
          currentPriceHigh,
          originalPriceLow = 0,
          originalPriceHigh = 0,
          comparePriceLow = 0,
          comparePriceHigh = 0,
          microData,
          vertical,
          spaceBetween,
          noStyle,
          subtraction,
          fontSize,
          fontSizePrevPrice = fontSize,
          fontWeight = 500,
          displayPriceLabels = false,
          rowBreakMainPrice = false,
          isPdp = false
        } = this.props;

        const culture = this.props.explicitCulture || this.props.culture;
        const currency = this.props.explicitCurrency || this.props.currency;
        let hasDiscount = listPrice > 0 && sellingPrice < listPrice;
        let hasCompare = listPrice > 0 && comparePrice > 0 && comparePrice !== listPrice;

        const baseProps: React.HTMLAttributes<HTMLSpanElement> = {};
        const priceProps: React.HTMLAttributes<HTMLSpanElement> = {};

        let priceRender = formatPrice(sellingPrice, currency, culture);
        let priceRenderOriginal = formatPrice(listPrice, currency, culture);
        let priceRenderCompare = comparePrice > 0 && formatPrice(comparePrice, currency, culture);

        if (typeof currentPriceLow === 'number' && typeof currentPriceHigh === 'number') {
          hasDiscount = false;
          hasCompare = false;
          if (originalPriceLow > 0 && originalPriceHigh > 0) {
            if (currentPriceLow < originalPriceLow || currentPriceHigh < originalPriceHigh) {
              hasDiscount = true;
            }

            if (comparePriceLow > 0 && comparePriceLow !== originalPriceLow && comparePriceHigh > 0 && comparePriceHigh !== originalPriceHigh) {
              hasCompare = true;
            }
          }

          priceRender = formatPrice(currentPriceLow, currency, culture, ([LanguageCodes.Finnish, LanguageCodes.German] as String[]).includes(culture)) + " - " + formatPrice(currentPriceHigh, currency, culture);
          priceRenderOriginal = formatPrice(originalPriceLow, currency, culture, ([LanguageCodes.Finnish, LanguageCodes.German] as String[]).includes(culture)) + " - " + formatPrice(originalPriceHigh, currency, culture);
          priceRenderCompare = comparePrice > 0 && formatPrice(comparePriceLow, currency, culture, ([LanguageCodes.Finnish, LanguageCodes.German] as String[]).includes(culture)) + " - " + formatPrice(comparePriceHigh, currency, culture);
        }

        if (microData) {
          baseProps.itemProp = 'offers';
          baseProps.itemType = 'http://schema.org/Offer';
          baseProps.itemScope = true;
        }

        let discountPercent = 0;
        if (hasDiscount) {
          discountPercent = Math.round(((listPrice - sellingPrice) / listPrice) * 100);
        }

        if (isPdp) {
          const offLabel = translate('/site/price/off');
          return (
            <PriceWrap {...baseProps}>
              {microData && <meta itemProp="priceCurrency" content={currency} />}
              <ItemPrice css={
                compose({
                  ...(hasDiscount && {
                    color: style.colors.functions.sale
                  })
                })
              }>
                <meta itemProp="price" content={sellingPrice?.toString()} />
                {priceRender}{discountPercent > 0 && <Percent><span>&middot;</span>{discountPercent + '%'}{offLabel && ' ' + offLabel}</Percent>}
              </ItemPrice>
              {hasDiscount &&
                <PrevPrice>
                  {translate('/site/price/abbr/original') + ' '}<PrevPricePrice>{priceRenderOriginal}</PrevPricePrice>
                </PrevPrice>
              }
              {hasCompare &&
                <ComparePrice>
                  {translate('/site/price/abbr/previous') + ' '}{priceRenderCompare}
                </ComparePrice>
              }
            </PriceWrap>
          );
        }

        return (
          <styled.Span
            {...baseProps}
            css={compose({
              ...(vertical
                ? {
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                }
                : {
                  display: 'inline-block',
                }),
              fontSize: `${fontSize}px`,
            })}
          >
            {microData && <meta itemProp="priceCurrency" content={currency} />}
            <styled.Span
              className="sg-reduced-prices"
              {...priceProps}
              css={compose({
                ...(!noStyle && {
                  fontWeight: fontWeight,
                }),
                display: 'inline-block',
                ...(rowBreakMainPrice && (hasCompare || hasDiscount) && {
                  display: 'block',
                }),
                ...(vertical &&
                  spaceBetween && {
                  marginBottom: 8,
                  lineHeight: 1,
                }),
                ...(((hasDiscount || hasCompare) && !vertical) && {
                  marginRight: '8px',
                }),
                ...(hasDiscount && {
                  color: style.colors.functions.sale
                })
              })}
            >
              <meta itemProp="price" content={sellingPrice?.toString()} />
              {subtraction && '-'}{priceRender}
            </styled.Span>
            {hasCompare && (
              <OldPrice
                css={compose({
                  fontWeight: fontWeight === 500 ? 400 : fontWeight,
                  ...(vertical && {
                    display: 'block',
                  }),
                  lineHeight: spaceBetween ? 1 : '100%',
                  textDecoration: 'line-through',
                  ...((hasDiscount && !vertical) && {
                    marginRight: '8px',
                  }),
                  fontSize: `${fontSizePrevPrice}px`,
                })}
              >
                {subtraction && '- '}{displayPriceLabels && (translate('/site/price/abbr/previous') + ' ')}{priceRenderCompare}
              </OldPrice>
            )}
            {hasDiscount && (
              <OldPrice
                css={compose({
                  fontWeight: fontWeight === 500 ? 400 : fontWeight,
                  ...(vertical && {
                    display: 'block',
                  }),
                  lineHeight: spaceBetween ? 1 : '100%',
                  textDecoration: 'line-through',
                  fontSize: `${fontSizePrevPrice}px`,
                })}
              >
                {subtraction && '- '}{displayPriceLabels && (translate('/site/price/abbr/original') + ' ')}{priceRenderOriginal}
              </OldPrice>
            )}
          </styled.Span>
        );
      }
    },
  ),
);
